import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(10px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}`;

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 10000;
  background: rgba(0, 0, 0, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 0.12s ease;
`;

const DialogCard = styled.div`
  width: min(400px, 100%);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.15s ease;
  overflow: hidden;
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h3`
  font-size: 15px; font-weight: 600; color: var(--text-primary); margin: 0;
`;

const Desc = styled.p`
  font-size: 13px; color: var(--text-secondary); margin: 0; line-height: 1.5;
`;

const Footer = styled.div`
  padding: 12px 24px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  border-top: 1px solid var(--border-subtle);
  background: var(--bg-primary);
`;

const Btn = styled.button<{ $danger?: boolean; $primary?: boolean }>`
  padding: 7px 16px; border-radius: 7px; font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: inherit;
  border: 1px solid ${({ $danger, $primary }) => $danger ? '#e5484d' : $primary ? 'var(--accent)' : 'var(--border-subtle)'};
  background: ${({ $danger, $primary }) => $danger ? '#e5484d' : $primary ? 'var(--accent)' : 'transparent'};
  color: ${({ $danger, $primary }) => ($danger || $primary) ? '#fff' : 'var(--text-secondary)'};
  transition: opacity 0.1s ease;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

type Props = {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ConfirmDialog: React.FC<Props> = ({
  open, title, description, confirmLabel = 'Подтвердить', cancelLabel = 'Отмена',
  danger = false, loading = false, onConfirm, onCancel,
}) => {
  if (!open) return null;
  return createPortal(
    <Backdrop onClick={onCancel}>
      <DialogCard onClick={(e) => e.stopPropagation()}>
        <Body>
          <Title>{title}</Title>
          {description && <Desc>{description}</Desc>}
        </Body>
        <Footer>
          <Btn onClick={onCancel}>{cancelLabel}</Btn>
          <Btn $danger={danger} $primary={!danger} disabled={loading} onClick={onConfirm}>
            {loading ? 'Загрузка...' : confirmLabel}
          </Btn>
        </Footer>
      </DialogCard>
    </Backdrop>,
    document.body,
  );
};
